import { computed, type MaybeRefOrGetter, toValue } from 'vue';
import { usePlanningStore } from '@/rennovation/planning-view/store/usePlanningStore';
import { STORAGE_KEYS } from '../constants';
import type { Driver } from '../types/models/user';

export function useDriverStatistics(driverSource: MaybeRefOrGetter<Driver | null>) {
	const planningStore = usePlanningStore();

	const driver = computed(() => toValue(driverSource));

	const stats = computed(() => {
		if (!driver.value) return null;

		const rawId = driver.value.id || driver.value._id;
		// Synthetic candidate-row ids ("realId_0") from multi-candidate views
		// (SolutionAssignDriver/SolutionAssignDuty) need to resolve back to the
		// real user id — statistics are only ever stored keyed by real ids.
		const targetId = rawId?.split('_')[0] ?? rawId;

		return planningStore.statistics?.[targetId] || null;
	});

	const overtimeHours = computed(() => {
		if (!stats.value?.overtimeBalance) return '--';
		return (stats.value.overtimeBalance / 3600).toFixed(2) + 'h';
	});

	const vacationDisplay = computed(() => {
		if (!stats.value?.vacationBalance) return '--';

		// Grab dynamic setup configurations right out of your reactive state wrapper
		const isHours = planningStore.setup?.planning_vacationInHours;
		return isHours ? (stats.value.vacationBalance / 3600).toFixed(2) + 'h' : stats.value.vacationBalance.toFixed(2) + 'd';
	});

	const satisfactionPercentage = computed(() => {
		if (!stats.value || stats.value.metSum === undefined) return null;
		const total = (stats.value.metSum || 0) + (stats.value.unmetSum || 0);
		return total === 0 ? 0 : Math.round((stats.value.metSum / total) * 100);
	});

	const satisfactionStatus = computed(() => {
		const val = satisfactionPercentage.value;
		if (val === null) return { icon: 'mdi-help-circle-outline', color: 'grey' };
		if (val < 40) return { icon: 'mdi-close-circle', color: 'red' };
		if (val <= 70) return { icon: 'mdi-minus-circle', color: 'blue' };
		return { icon: 'mdi-check-circle', color: 'green' };
	});

	const getPensum = (day, driver) => {
		let pensum = 0;
		if (!day || !driver) {
			return pensum;
		}
		const selectedDay = new Date(day);
		const dateOfEntry = driver.dateOfEntry ? new Date(driver.dateOfEntry) : null;
		const dateOfLeaving = driver.dateOfLeaving ? new Date(driver.dateOfLeaving) : null;

		if (!((dateOfEntry && dateOfEntry > selectedDay) || (dateOfLeaving && dateOfLeaving < selectedDay))) {
			if (driver.pensumHistory && Array.isArray(driver.pensumHistory) && driver.pensumHistory.length > 0) {
				const pensumHistory = [...driver.pensumHistory];
				pensumHistory.sort((a, b) => {
					const dateA = new Date(a.validFrom);
					const dateB = new Date(b.validFrom);
					return dateA - dateB;
				});

				for (let i = pensumHistory?.length - 1; i >= 0; i--) {
					if (selectedDay >= new Date(pensumHistory[i].validFrom)) {
						pensum = pensumHistory[i].amount;
						i = -1;
					}
				}
			} else {
				pensum = 100;
			}
		}
		return pensum;
	};

	const getDay = () => {
		const planningData = JSON.parse(localStorage.getItem(STORAGE_KEYS.PLANNING_TABLE_DATA));
		const day = planningData.selectedDay;
		return day?.dateString;
	};

	const getStatValue = (key: string) => {
		if (!driver.value) return '--';

		switch (key) {
			case 'work_load':
				return `${getPensum(getDay(), driver.value) || 0}%`;
			case 'work_time':
				return overtimeHours.value;
			case 'rest_day':
				return `${stats.value?.restDayBalance?.toFixed(2) || '--'}d`;
			case 'vacation':
				return vacationDisplay.value;
			default:
				return '--';
		}
	};

	return {
		stats,
		overtimeHours,
		vacationDisplay,
		satisfactionStatus,
		getStatValue,
	};
}
